/**
 * Builds the Excel/Word/PDF export for the Asset Sales & Purchases register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered register:
 * one row per active transaction, matching the table columns.
 *
 * Money is written as a real number and dates as real Date cells so the
 * practitioner can sort / sum in Excel.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import type { AssetTransactionUI } from "./types";

function asDate(raw?: string): Date | "" {
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
}

function transactionsSheet(entries: AssetTransactionUI[]): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active")
    .sort((a, b) => {
      const aTime = a.transactionDate
        ? new Date(a.transactionDate).getTime()
        : 0;
      const bTime = b.transactionDate
        ? new Date(b.transactionDate).getTime()
        : 0;
      const aSafe = Number.isNaN(aTime) ? 0 : aTime;
      const bSafe = Number.isNaN(bTime) ? 0 : bTime;
      if (aSafe !== bSafe) return aSafe - bSafe;
      return a.counterpartyName.localeCompare(b.counterpartyName);
    });

  return {
    name: "Asset Sales & Purchases",
    headers: [
      "Date",
      "Counterparty",
      "Asset class",
      "Asset",
      "Identifier",
      "Direction",
      "Net Amount",
      "Agreement",
      "Review",
    ],
    rows: sorted.map((entry) => [
      asDate(entry.transactionDate),
      entry.counterpartyName,
      entry.assetClass ?? "",
      entry.assetDescription,
      entry.assetIdentifier ?? "",
      entry.direction,
      entry.amountNet,
      entry.agreementStatus,
      entry.reviewStatus,
    ]),
  };
}

export function buildAssetSalesPurchasesExport(
  entries: AssetTransactionUI[],
): ExportContent {
  return {
    title: "Asset Sales & Purchases",
    sheets: [transactionsSheet(entries)],
  };
}
